import DropdownMenu, { ActionsProps } from "../../Inputs/DropdownMenu";
import { ButtonLink } from "../ButtonLink";
import { StyleVariant, Tag } from "../Tag";
import { Body } from "../Typography";
import {
  CollaboratorActions,
  CollaboratorContent,
  CollaboratorDescriptionRow,
  CollaboratorRowContainer,
  CollaboratorTextContent,
} from "./__styles__/CollaboratorList";
import { Collaborator, PermissionOption, RoleOption } from "./types";

interface CollaboratorRowProps {
  collaborator: Collaborator;
  permissionOptions: PermissionOption[];
  onPermissionChange: (collaboratorId: string, permission: string) => void;
  onRemove: (collaboratorId: string) => void;
  showPermissionOptions?: boolean;
  roleOptions?: RoleOption[];
  onRoleChange?: (
    collaboratorId: string,
    role: string,
    userGroupId: string,
  ) => void;
}

const STATUS_BADGE_CONFIG: Record<
  string,
  { variant: StyleVariant; label: string }
> = {
  inviteSent: { variant: "neutral", label: "Invite sent" },
  unconfirmed: { variant: "warning", label: "Unconfirmed" },
  pending: { variant: "warning", label: "Pending" },
  suspended: { variant: "critical", label: "Suspended" },
  denied: { variant: "critical", label: "Denied" },
};

export const CollaboratorRow = ({
  collaborator,
  permissionOptions,
  onPermissionChange,
  onRemove,
  showPermissionOptions = true,
  roleOptions,
  onRoleChange,
}: CollaboratorRowProps) => {
  const currentPermissionLabel =
    permissionOptions.find(opt => opt.value === collaborator.permission)
      ?.label || "Can view";
  // In the share workflow UI, we show the public user's group name (e.g., "Homeowner")
  // instead of a permission level since only "view" applies.
  const displayLabel =
    showPermissionOptions ?
      currentPermissionLabel
    : (collaborator.role ?? "Unknown");

  const statusBadge =
    collaborator.entityType !== "emailInvite" && collaborator.status ?
      STATUS_BADGE_CONFIG[collaborator.status]
    : undefined;

  const needsRoleSelection =
    collaborator.entityType === "emailInvite" &&
    !collaborator.role &&
    roleOptions?.length;

  const dropdownActions: ActionsProps[] = [
    {
      label: "Remove",
      onClick: () => onRemove(collaborator.id),
      variant: "red" as const,
    },
  ];

  if (showPermissionOptions) {
    dropdownActions.push(
      ...permissionOptions.map(option => ({
        label: option.label,
        onClick: () => onPermissionChange(collaborator.id, option.value),
      })),
    );
  }

  const roleDropdownActions: ActionsProps[] = [
    ...(roleOptions ?? []).map(role => ({
      label: role.name,
      onClick: () => onRoleChange?.(collaborator.id, role.name, role.id),
    })),
    {
      label: "Remove",
      onClick: () => onRemove(collaborator.id),
      variant: "red" as const,
    },
  ];

  return (
    <CollaboratorRowContainer>
      <CollaboratorContent>
        <CollaboratorTextContent>
          <CollaboratorDescriptionRow>
            <Body type="emphasis" size="default" color="contentPrimary">
              {collaborator.name}
            </Body>
            {statusBadge && (
              <Tag styleVariant={statusBadge.variant} size="in-line">
                {statusBadge.label}
              </Tag>
            )}
          </CollaboratorDescriptionRow>
          {collaborator.entityType === "publicUserInvite" ?
            <Body type="regular" size="default" color="contentSecondary">
              Invite sent
            </Body>
          : collaborator.description &&
            collaborator.description !== collaborator.name && (
              <Body type="regular" size="default" color="contentSecondary">
                {collaborator.description}
              </Body>
            )
          }
        </CollaboratorTextContent>
      </CollaboratorContent>
      <CollaboratorActions>
        {needsRoleSelection ?
          <DropdownMenu
            actions={roleDropdownActions}
            position="below-left"
            customButton={({ onPress }) => (
              <ButtonLink
                styleVariant="primary"
                size="small"
                rightIconName="chevron-down"
                onClick={onPress}
              >
                Select role
              </ButtonLink>
            )}
          />
        : <DropdownMenu
            actions={dropdownActions}
            position="below-left"
            customButton={({ onPress }) => (
              <ButtonLink
                styleVariant="primary"
                size="small"
                rightIconName="chevron-down"
                onClick={onPress}
              >
                {displayLabel}
              </ButtonLink>
            )}
          />
        }
      </CollaboratorActions>
    </CollaboratorRowContainer>
  );
};
